import React, {
  createContext,
  FC,
  ReactNode,
  RefObject,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useToast } from 'Shared/components';
import { PaymentDetailsType } from 'QuoteApp/services/endpoints/GetPaymentDetails';
import { useAppContext } from '../AppProvider';
import { usePurchaseSubmit, PurchaseSubmitType } from './hooks';
import { useSignaturePacket } from '../AppProvider/hooks/useSignaturePacket';
import { useCheckoutLink } from '../AppProvider/hooks/useCheckoutLink';
import { PostSignaturePacketResponse } from 'QuoteApp/services/endpoints/PostSignaturePacket';
import {
  PostCheckoutLinkRequestType,
  PostCheckoutLinkResponseType,
} from 'QuoteApp/services/endpoints/PostCheckoutLink';
import { GetSignaturePacketSendAttemptResposne } from 'QuoteApp/services/endpoints/GetSignaturePacketSendAttemptResposne';
import { useAgentConfirmation } from 'Shared/hooks/useAgentConfirmation';
import { GetDetailsResponseType } from 'QuoteApp/services/endpoints/GetDetails';
import { GetDisclosuresResponseType } from 'QuoteApp/services/endpoints/GetDisclosures';

export type ESignatureErrorsType = {
  smsConsent?: string;
  sentSingnaturePacket?: string;
  agentConfirmation?: string;
  sweepAffirmation?: string;
};

export type PurchaseProviderType = {
  appDataStale: PurchaseSubmitType['appDataStale'];
  clearErrors: PurchaseSubmitType['clearErrors'];
  disableAgentSignatures: boolean;
  errors: PurchaseSubmitType['errors'];
  isEditingContactInfo: boolean;
  onPaymentError(message?: string): void;
  paymentDetails: PaymentDetailsType | undefined;
  paymentMethodsSectionRef: RefObject<HTMLDivElement>;
  processOneBind: PurchaseSubmitType['processOneBind'];
  processing: PurchaseSubmitType['processing'];
  setIsEditingContactInfo(isEditing: boolean): void;
  submitBind: PurchaseSubmitType['submitBind'];
  eSignatureSMSConsent: boolean | undefined;
  setESignatureSMSConsent(consent: boolean): void;
  setESignatureErrors(error: ESignatureErrorsType | null): void;
  eSignatureErrors: ESignatureErrorsType | null;
  scrollIntoESignatureView(): void;
  eSignatureSectionRef: RefObject<HTMLDivElement>;
  agentConfirmation: boolean;
  setAgentConfirmation(confirmation: boolean): void;
  sendSignaturePacket(
    sendLinkBySms: boolean
  ): Promise<PostSignaturePacketResponse | undefined>;
  getSignaturePacket(): Promise<
    GetSignaturePacketSendAttemptResposne | undefined
  >;
  sendSingnaturePacketInProgress: boolean;
  loadingSignaturePacket: boolean;
  setSignaturePacketSendAttempt: (
    signaturePacket: GetSignaturePacketSendAttemptResposne | null
  ) => void;
  signaturePacketSendAttempt: GetSignaturePacketSendAttemptResposne | null;
  sendCheckoutLink: (
    params: PostCheckoutLinkRequestType
  ) => Promise<PostCheckoutLinkResponseType>;
  sendCheckoutLinkInProgress: boolean;
  postConfirmation: () => Promise<GetDetailsResponseType>;
  agentSignatureAffirmation: GetDisclosuresResponseType | null;
  agentSignatureAffirmationDataLoading: boolean;
  agentBindAuthorization: GetDisclosuresResponseType | null;
  agentBindAuthorizationDataLoading: boolean;
  agentTOS: GetDisclosuresResponseType | null;
  agentTOSDataLoading: boolean;
  vehiclePhotos: GetDisclosuresResponseType | null;
  vehiclePhotosDataLoading: boolean;
};

export const PurchaseProviderContext = createContext<
  PurchaseProviderType | undefined
>(undefined);

export const usePurchaseContext = () => {
  // https://stackoverflow.com/a/69735347
  const purchaseProviderContext = useContext(PurchaseProviderContext);
  if (!purchaseProviderContext)
    throw new Error(
      'No PurchaseProviderContext.Provider found when calling usePurchaseContext.'
    );
  return purchaseProviderContext;
};

export const PurchaseProvider: FC<{
  children: (ctx: PurchaseProviderType) => ReactNode;
}> = ({ children }) => {
  const { toast } = useToast();
  const { appId, rate, details } = useAppContext();
  const disableAgentSignatures = !details.commands.sendSignaturePacket;
  const paymentMethodsSectionRef = useRef<HTMLDivElement>(null);
  const scrollErrorsIntoView = useCallback(
    () =>
      paymentMethodsSectionRef?.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      }),
    []
  );
  const eSignatureSectionRef = useRef<HTMLDivElement>(null);
  const scrollIntoESignatureView = useCallback(
    () =>
      eSignatureSectionRef?.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      }),
    []
  );

  const onPaymentError = useCallback(
    (message?: string) => {
      scrollErrorsIntoView();
      toast({
        type: 'error',
        message:
          message ?? 'Please fix errors on payment information to submit.',
        withSecondaryNav: false,
        icon: 'error',
      });
    },
    [scrollErrorsIntoView, toast]
  );

  const [isEditingContactInfo, setIsEditingContactInfo] = useState<boolean>(
    false
  );

  const [eSignatureSMSConsent, setESignatureSMSConsent] = useState<boolean>();
  const [
    eSignatureErrors,
    setESignatureErrors,
  ] = useState<ESignatureErrorsType | null>(null);
  const [agentConfirmation, setAgentConfirmation] = useState<boolean>(false);

  const paymentDetails = useMemo(() => rate?.paymentDetails?.attributes, [
    rate,
  ]);

  const {
    appDataStale,
    clearErrors,
    errors,
    processOneBind,
    processing,
    submitBind,
  } = usePurchaseSubmit(appId, scrollErrorsIntoView);

  const {
    sendSignaturePacket,
    getSignaturePacket,
    sendSingnaturePacketInProgress,
    loadingSignaturePacket,
    setSignaturePacketSendAttempt,
    signaturePacketSendAttempt,
  } = useSignaturePacket(appId);

  const { sendCheckoutLink, sendCheckoutLinkInProgress } = useCheckoutLink(
    appId
  );

  const {
    postConfirmation,
    agentSignatureAffirmation,
    agentSignatureAffirmationDataLoading,
    agentBindAuthorization,
    agentBindAuthorizationDataLoading,
    agentTOS,
    agentTOSDataLoading,
    vehiclePhotos,
    vehiclePhotosDataLoading,
  } = useAgentConfirmation(appId, disableAgentSignatures);

  const providerValues = useMemo<PurchaseProviderType>(
    () => ({
      appDataStale,
      clearErrors,
      disableAgentSignatures,
      errors,
      isEditingContactInfo,
      onPaymentError,
      paymentDetails,
      paymentMethodsSectionRef: paymentMethodsSectionRef as RefObject<
        HTMLDivElement
      >,
      processOneBind,
      processing,
      setIsEditingContactInfo,
      submitBind,
      eSignatureSMSConsent,
      setESignatureSMSConsent,
      setESignatureErrors,
      eSignatureErrors,
      scrollIntoESignatureView,
      eSignatureSectionRef: eSignatureSectionRef as RefObject<HTMLDivElement>,
      agentConfirmation,
      setAgentConfirmation,
      sendSignaturePacket,
      getSignaturePacket,
      sendSingnaturePacketInProgress,
      loadingSignaturePacket,
      setSignaturePacketSendAttempt,
      signaturePacketSendAttempt,
      sendCheckoutLink,
      sendCheckoutLinkInProgress,
      postConfirmation,
      agentSignatureAffirmation,
      agentSignatureAffirmationDataLoading,
      agentBindAuthorization,
      agentBindAuthorizationDataLoading,
      agentTOS,
      agentTOSDataLoading,
      vehiclePhotos,
      vehiclePhotosDataLoading,
    }),
    [
      appDataStale,
      clearErrors,
      disableAgentSignatures,
      errors,
      isEditingContactInfo,
      onPaymentError,
      paymentDetails,
      processOneBind,
      processing,
      submitBind,
      eSignatureSMSConsent,
      setESignatureErrors,
      eSignatureErrors,
      scrollIntoESignatureView,
      eSignatureSectionRef,
      agentConfirmation,
      setAgentConfirmation,
      sendSignaturePacket,
      getSignaturePacket,
      sendSingnaturePacketInProgress,
      loadingSignaturePacket,
      setSignaturePacketSendAttempt,
      signaturePacketSendAttempt,
      sendCheckoutLink,
      sendCheckoutLinkInProgress,
      postConfirmation,
      agentSignatureAffirmation,
      agentSignatureAffirmationDataLoading,
      agentBindAuthorization,
      agentBindAuthorizationDataLoading,
      agentTOS,
      agentTOSDataLoading,
      vehiclePhotos,
      vehiclePhotosDataLoading,
    ]
  );

  return (
    <PurchaseProviderContext.Provider value={providerValues}>
      {children(providerValues)}
    </PurchaseProviderContext.Provider>
  );
};
